import { useEffect, useRef, useState } from 'react';

interface UseScrollParentOptions {
  /**
   * Only accept an ancestor whose content overflows right now (the default). A
   * virtualized list passes false: its container is the scroller before the
   * list is long enough to overflow it.
   */
  requireOverflow?: boolean;
}

const scrollsVertically = (el: HTMLElement): boolean => {
  const overflowY = getComputedStyle(el).overflowY;
  return overflowY === 'auto' || overflowY === 'scroll';
};

/**
 * Finds the nearest scrollable ancestor of a sentinel element. Dashboard pages
 * live inside an overflow:auto container (the app's `<main>`, a tab panel, a
 * drawer body), so scroll-driven features must observe that ancestor rather
 * than the window. Returns a ref to attach to a sentinel plus the resolved
 * scroll container (null until mounted, or when the page itself scrolls).
 */
export const useScrollParent = ({ requireOverflow = true }: UseScrollParentOptions = {}) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);

  useEffect(() => {
    let node = sentinelRef.current?.parentElement ?? null;
    while (node && node !== document.body) {
      if (scrollsVertically(node) && (!requireOverflow || node.scrollHeight > node.clientHeight)) {
        setScrollParent(node);
        return;
      }
      node = node.parentElement;
    }
    setScrollParent(null);
  }, [requireOverflow]);

  return { sentinelRef, scrollParent };
};
